// /app/frontend/src/pages/risk/DPIAWorkflowPage.jsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import { useAuth } from '../../contexts/AuthContext';
import { dpiaAPI, entitiesAPI, riskLibraryAPI, controlLibraryAPI } from '../../services/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { ScrollArea } from '../../components/ui/scroll-area';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { AutoTaskPromptModal } from '../../components/shared/AutoTaskPromptModal';
import { TasksSection } from '../../components/shared/TasksSection';
import { 
  ArrowLeft, Save, Users, Scale,
  Plus, ChevronRight, ChevronLeft, Loader2, Check, Search,
  Settings, DollarSign, Library, Shield, Download
} from 'lucide-react';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger
} from '../../components/ui/dropdown-menu';
import { FormStepper, StepperProgress } from '../../components/ui/form-stepper';
import { AlertTriangle, FileText } from 'lucide-react';

// Import step components
import {
  ProjectDetailsStep,
  ScopeStep,
  DataStep,
  LawfulBasisStep,
  DataSharingStep,
  TransfersStep,
  ConsultationStep,
  NecessityStep,
  RisksStep,
  OutcomeStep,
  ApprovalsStep
} from './dpia/steps';
import AlertFlagsBanner from './dpia/components/AlertFlagsBanner';

// Import constants
import { TABS, getInitialFormData, getRiskLevel } from './dpia/constants';

export default function DPIAWorkflowPage() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, isComplianceReadOnly: isReadOnly } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [entities, setEntities] = useState([]);
  const [currentTab, setCurrentTab] = useState(1);
  const [formData, setFormData] = useState(getInitialFormData(searchParams.get('entity')));
  const [isNew, setIsNew] = useState(!id);
  const [lastSaved, setLastSaved] = useState(null);
  const [taskPromptOpen, setTaskPromptOpen] = useState(false);
  const [taskPromptDpiaId, setTaskPromptDpiaId] = useState(null);
  const isDpoRole = ['dpo', 'super_admin'].includes(user?.role);
  
  // Risk Library Dialog State
  const [riskLibraryOpen, setRiskLibraryOpen] = useState(false);
  const [libraryRisks, setLibraryRisks] = useState([]);
  const [libraryControls, setLibraryControls] = useState([]);
  const [librarySearch, setLibrarySearch] = useState('');
  const [libraryCategory, setLibraryCategory] = useState('all');
  const [selectedLibraryRisks, setSelectedLibraryRisks] = useState([]);
  const [loadingLibrary, setLoadingLibrary] = useState(false);
  const [fuzzyRankings, setFuzzyRankings] = useState({}); // risk_id -> relevance data
  const [showSuggestedOnly, setShowSuggestedOnly] = useState(true);
  
  // Real-time fuzzy estimation state
  const [estimatingRisks, setEstimatingRisks] = useState({}); // risk_id -> boolean (loading state)
  const controlEstimateTimers = useRef({}); // Debounce timers for each risk
  
  // Control Library Dialog State
  const [controlLibraryOpen, setControlLibraryOpen] = useState(false);
  const [controlSearch, setControlSearch] = useState('');
  const [controlCategory, setControlCategory] = useState('all');
  const [selectedControls, setSelectedControls] = useState([]);
  const [loadingControls, setLoadingControls] = useState(false);
  const [activeRiskForControls, setActiveRiskForControls] = useState(null); // Which risk we're adding controls to
  const [allControls, setAllControls] = useState([]); // Full control library

  useEffect(() => {
    loadData();
  }, [id]);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Load entities
      const entitiesRes = await entitiesAPI.getAll();
      setEntities(entitiesRes.data);
      
      // Load existing DPIA if editing
      if (id) {
        const dpiaRes = await dpiaAPI.getOne(id);
        setFormData(dpiaRes.data);
        setCurrentTab(dpiaRes.data.current_step || 1);
        setIsNew(false);
      } else {
        const entityId = searchParams.get('entity');
        if (entityId) {
          setFormData(prev => ({ ...prev, entity_id: entityId }));
        }
        setIsNew(true);
      }
    } catch (error) {
      console.error('Failed to load data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (showToast = true) => {
    try {
      setSaving(true);
      
      // Calculate risk scores before saving
      const updatedData = { ...formData, current_step: currentTab };
      
      if (updatedData.risks.length > 0) {
        const totalBefore = updatedData.risks.reduce((sum, r) => sum + (r.score_before || 0), 0);
        const totalAfter = updatedData.risks.reduce((sum, r) => sum + (r.score_after || 0), 0);
        const avgBefore = totalBefore / updatedData.risks.length;
        const avgAfter = totalAfter / updatedData.risks.length;
        
        updatedData.initial_project_risk = Math.round(avgBefore * 10) / 10;
        updatedData.project_risk_after_controls = Math.round(avgAfter * 10) / 10;
        updatedData.risk_reduction_points = Math.round((avgBefore - avgAfter) * 10) / 10;
        updatedData.risk_reduction_percent = avgBefore > 0 
          ? Math.round((avgBefore - avgAfter) / avgBefore * 100) 
          : 0;
      }
      
      let savedId = id;
      if (isNew) {
        const response = await dpiaAPI.create(updatedData);
        savedId = response.data.id;
        setFormData(response.data);
        setIsNew(false);
        navigate(`/risk/dpia/${savedId}`, { replace: true });
        if (showToast) toast.success('DPIA created successfully');
      } else {
        await dpiaAPI.update(id, updatedData);
        if (showToast) toast.success('DPIA saved');
      }
      setLastSaved(new Date());

      // Spec Section 5: offer auto-generated remediation tasks on manual save when high/extreme findings exist
      if (showToast && isDpoRole && savedId) {
        const hasHighRiskFindings = (updatedData.risks || []).some((r) => {
          const level = (r.residual_risk_level || r.risk_level || '').toLowerCase();
          return level === 'high' || level === 'extreme' || level === 'critical';
        });
        if (hasHighRiskFindings) {
          setTaskPromptDpiaId(savedId);
          setTaskPromptOpen(true);
        }
      }
    } catch (error) {
      console.error('Failed to save:', error);
      toast.error('Failed to save DPIA');
    } finally {
      setSaving(false);
    }
  };

  // Export DPIA to DOCX
  const handleExport = async (exportType = 'full') => {
    if (isNew || !id) {
      toast.error('Please save the DPIA before exporting');
      return;
    }
    
    try {
      setExporting(true);
      const response = await dpiaAPI.exportDocx(id, exportType);
      
      // Create blob and download
      const blob = new Blob([response.data], { 
        type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' 
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const filename = exportType === 'summary' 
        ? `DPIA_Summary_${formData.dpia_reference || 'export'}.docx`
        : `DPIA_${formData.dpia_reference || 'export'}.docx`;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success(`DPIA ${exportType === 'summary' ? 'summary' : 'report'} exported successfully`);
    } catch (error) {
      console.error('Failed to export DPIA:', error);
      toast.error('Failed to export DPIA');
    } finally {
      setExporting(false);
    }
  };

  // Auto-save effect - saves 2 seconds after last change (only if not new and has data)
  useEffect(() => {
    if (isNew || loading) return;
    
    const autoSaveTimer = setTimeout(() => {
      if (formData.project_title || formData.project_description) {
        handleSave(false); // Silent save
      }
    }, 2000);
    
    return () => clearTimeout(autoSaveTimer);
  }, [formData, isNew, loading]);

  const updateField = (field, value) => {
    if (isReadOnly) return; // Prevent updates for read-only users
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const nextTab = async () => {
    await handleSave(false);
    if (currentTab < TABS.length) {
      setCurrentTab(currentTab + 1);
    }
  };

  const prevTab = () => {
    if (currentTab > 1) {
      setCurrentTab(currentTab - 1);
    }
  };

  // ==================== Data Item Handlers ====================
  const addDataItem = () => {
    if (isReadOnly) return;
    const newItem = {
      id: uuidv4(),
      data_collected: '',
      data_source: '',
      why_required: '',
      how_processed: '',
      storage_location: '',
      data_state: 'identifiable',
      retention_period: '',
      retention_reason: '',
    };
    updateField('data_items', [...formData.data_items, newItem]);
  };

  const updateDataItem = (itemId, field, value) => {
    const updated = formData.data_items.map(item => 
      item.id === itemId ? { ...item, [field]: value } : item
    );
    updateField('data_items', updated);
  };

  const removeDataItem = (itemId) => {
    if (isReadOnly) return;
    updateField('data_items', formData.data_items.filter(item => item.id !== itemId));
  };

  // ==================== Third Party Handlers ====================
  const addThirdParty = () => {
    if (isReadOnly) return;
    const newParty = {
      id: uuidv4(),
      name: '',
      role: 'Processor',
      contract_in_place: false,
      dpa_in_place: false,
      security_commitments: '',
    };
    updateField('third_parties', [...formData.third_parties, newParty]);
  };

  const updateThirdParty = (partyId, field, value) => {
    const updated = formData.third_parties.map(party => 
      party.id === partyId ? { ...party, [field]: value } : party
    );
    updateField('third_parties', updated);
  };

  const removeThirdParty = (partyId) => {
    if (isReadOnly) return;
    updateField('third_parties', formData.third_parties.filter(p => p.id !== partyId));
  };

  // ==================== Risk Handlers ====================
  
  // Control estimation removed - using simple Impact × Likelihood calculation
  // This function is kept as a stub for backward compatibility with any debounced calls
  const estimateControlEffectiveness = useCallback(async (riskId, existingControls, impactBefore, likelihoodBefore, libraryRiskId) => {
    // Fuzzy estimation removed - users manually set After Controls values
    return null;
  }, []);
  
  const addRisk = () => {
    if (isReadOnly) return;
    const newRisk = {
      id: uuidv4(),
      risk_number: formData.risks.length + 1,
      description: '',
      impact_before: 3,
      likelihood_before: 3,
      score_before: 9,
      level_before: 'serious',
      existing_controls: '',
      impact_after: 2,
      likelihood_after: 2,
      score_after: 4,
      level_after: 'moderate',
      additional_controls: '',
      measures_approved: false,
      control_owner: '',
      implementation_date: '',
    };
    updateField('risks', [...formData.risks, newRisk]);
  };

  const updateRisk = (riskId, field, value) => {
    const updated = formData.risks.map(risk => {
      if (risk.id !== riskId) return risk;
      
      const updatedRisk = { ...risk, [field]: value };
      
      // Recalculate scores when impact/likelihood changes
      if (field.includes('impact_') || field.includes('likelihood_')) {
        if (field.includes('before')) {
          updatedRisk.score_before = updatedRisk.impact_before * updatedRisk.likelihood_before;
          updatedRisk.level_before = getRiskLevel(updatedRisk.score_before);
        } else {
          updatedRisk.score_after = updatedRisk.impact_after * updatedRisk.likelihood_after;
          updatedRisk.level_after = getRiskLevel(updatedRisk.score_after);
        }
      }
      
      return updatedRisk;
    });
    updateField('risks', updated);
    
    // Trigger debounced fuzzy estimation when existing_controls changes
    if (field === 'existing_controls') {
      const risk = formData.risks.find(r => r.id === riskId);
      if (risk?.library_risk_id) {
        // Clear any existing timer for this risk
        if (controlEstimateTimers.current[riskId]) {
          clearTimeout(controlEstimateTimers.current[riskId]);
        }
        
        // Set a new debounced timer (800ms delay)
        controlEstimateTimers.current[riskId] = setTimeout(() => {
          estimateControlEffectiveness(
            riskId,
            value, // The new existing_controls text
            risk.impact_before,
            risk.likelihood_before,
            risk.library_risk_id
          );
        }, 800);
      }
    }
    
    // Also trigger estimation when impact_before or likelihood_before changes (for library risks with controls)
    if ((field === 'impact_before' || field === 'likelihood_before') && value) {
      const risk = formData.risks.find(r => r.id === riskId);
      if (risk?.library_risk_id && risk.existing_controls && risk.existing_controls.trim().length >= 3) {
        // Debounce the re-estimation
        if (controlEstimateTimers.current[riskId]) {
          clearTimeout(controlEstimateTimers.current[riskId]);
        }
        
        controlEstimateTimers.current[riskId] = setTimeout(() => {
          const updatedRisk = updated.find(r => r.id === riskId);
          estimateControlEffectiveness(
            riskId,
            updatedRisk.existing_controls,
            updatedRisk.impact_before,
            updatedRisk.likelihood_before,
            updatedRisk.library_risk_id
          );
        }, 800);
      }
    }
  };

  const removeRisk = (riskId) => {
    if (isReadOnly) return;
    const updated = formData.risks
      .filter(r => r.id !== riskId)
      .map((r, idx) => ({ ...r, risk_number: idx + 1 }));
    updateField('risks', updated);
  };

  // ==================== Risk Library Functions ====================
  const openRiskLibrary = async () => {
    setRiskLibraryOpen(true);
    setLoadingLibrary(true);
    setSelectedLibraryRisks([]);
    setShowSuggestedOnly(true);
    try {
      const [risksRes, controlsRes] = await Promise.all([
        riskLibraryAPI.getAll(),
        controlLibraryAPI.getAll(),
      ]);
      setLibraryRisks(risksRes.data);
      setLibraryControls(controlsRes.data);
      
      // Note: Fuzzy risk suggestions removed - risks are shown without fuzzy ranking
      setFuzzyRankings({});
    } catch (error) {
      console.error('Failed to load risk library:', error);
      toast.error('Failed to load risk library');
    } finally {
      setLoadingLibrary(false);
    }
  };

  const toggleRiskSelection = (risk) => {
    setSelectedLibraryRisks(prev => {
      const isSelected = prev.find(r => r.risk_id === risk.risk_id);
      if (isSelected) {
        return prev.filter(r => r.risk_id !== risk.risk_id);
      }
      return [...prev, risk];
    });
  };

  const getControlsForRisk = (riskId) => {
    return libraryControls.filter(c => c.control_id === riskId);
  };

  const addRisksFromLibrary = () => {
    const newRisks = selectedLibraryRisks.map((libRisk, idx) => {
      const controls = getControlsForRisk(libRisk.risk_id);
      const existingControlsText = controls.slice(0, 3).map(c => c.control_name).join(', ');
      
      // Calculate impact and likelihood from default score (score = impact * likelihood)
      // Using square root approximation for balanced values
      const sqrtScore = Math.sqrt(libRisk.default_score);
      const impact = Math.min(5, Math.max(1, Math.round(sqrtScore)));
      const likelihood = Math.min(5, Math.max(1, Math.round(libRisk.default_score / impact)));
      
      return {
        id: uuidv4(),
        risk_number: formData.risks.length + idx + 1,
        library_risk_id: libRisk.risk_id,
        description: `[${libRisk.risk_id}] ${libRisk.risk_name}: ${libRisk.description}`,
        impact_before: impact,
        likelihood_before: likelihood,
        score_before: impact * likelihood,
        level_before: getRiskLevel(impact * likelihood),
        existing_controls: existingControlsText || '',
        impact_after: Math.max(1, impact - 1),
        likelihood_after: Math.max(1, likelihood - 1),
        score_after: Math.max(1, impact - 1) * Math.max(1, likelihood - 1),
        level_after: getRiskLevel(Math.max(1, impact - 1) * Math.max(1, likelihood - 1)),
        additional_controls: '',
        measures_approved: false,
        control_owner: '',
        implementation_date: '',
      };
    });
    
    updateField('risks', [...formData.risks, ...newRisks]);
    setRiskLibraryOpen(false);
    setSelectedLibraryRisks([]);
    toast.success(`Added ${newRisks.length} risk(s) from library`);
  };

  const filteredLibraryRisks = libraryRisks
    .filter(risk => {
      const matchesSearch = !librarySearch || 
        risk.risk_name.toLowerCase().includes(librarySearch.toLowerCase()) ||
        risk.description.toLowerCase().includes(librarySearch.toLowerCase()) ||
        risk.risk_id.toLowerCase().includes(librarySearch.toLowerCase());
      const matchesCategory = libraryCategory === 'all' || risk.category === libraryCategory;
      
      // If showing suggested only, filter by fuzzy suggestion level
      if (showSuggestedOnly && Object.keys(fuzzyRankings).length > 0) {
        const ranking = fuzzyRankings[risk.risk_id];
        if (ranking && ['auto_selected', 'suggested'].includes(ranking.suggestion_level)) {
          return matchesSearch && matchesCategory;
        }
        return false;
      }
      
      return matchesSearch && matchesCategory;
    })
    .sort((a, b) => {
      // Sort by fuzzy relevance if available
      const rankA = fuzzyRankings[a.risk_id]?.relevance_score || 0;
      const rankB = fuzzyRankings[b.risk_id]?.relevance_score || 0;
      return rankB - rankA;
    });

  // ==================== Approver Handlers ====================
  const addApprover = () => {
    const newApprover = {
      id: uuidv4(),
      name: '',
      position: '',
      approval_date: '',
      comments: '',
      approved: false,
    };
    updateField('approvers', [...formData.approvers, newApprover]);
  };

  const updateApprover = (approverId, field, value) => {
    const updated = formData.approvers.map(approver => 
      approver.id === approverId ? { ...approver, [field]: value } : approver
    );
    updateField('approvers', updated);
  };

  const removeApprover = (approverId) => {
    updateField('approvers', formData.approvers.filter(a => a.id !== approverId));
  };

  // ==================== Control Library Functions ====================
  const openControlLibrary = async (riskId) => {
    setActiveRiskForControls(riskId);
    setControlLibraryOpen(true);
    setLoadingControls(true);
    setSelectedControls([]);
    setControlSearch('');
    setControlCategory('all');
    
    try {
      const res = await controlLibraryAPI.getAll();
      setAllControls(res.data);
    } catch (error) {
      console.error('Failed to load control library:', error);
      toast.error('Failed to load control library');
    } finally {
      setLoadingControls(false);
    }
  };

  const toggleControlSelection = (control) => {
    setSelectedControls(prev => {
      const key = `${control.control_id}-${control.control_name}`;
      const isSelected = prev.find(c => `${c.control_id}-${c.control_name}` === key);
      if (isSelected) {
        return prev.filter(c => `${c.control_id}-${c.control_name}` !== key);
      }
      return [...prev, control];
    });
  };

  const addControlsFromLibrary = () => {
    if (!activeRiskForControls || selectedControls.length === 0) return;
    
    const risk = formData.risks.find(r => r.id === activeRiskForControls);
    if (!risk) return;
    
    // Merge selected controls with existing ones
    const existingControls = risk.selected_controls || [];
    const newControls = selectedControls.filter(sc => 
      !existingControls.some(ec => 
        ec.control_id === sc.control_id && ec.control_name === sc.control_name
      )
    );
    
    const updatedControls = [...existingControls, ...newControls];
    const controlNames = updatedControls.map(c => c.control_name).join(', ');
    
    // Update the risk with both selected_controls and existing_controls in one operation
    const updatedRisks = formData.risks.map(r => {
      if (r.id !== activeRiskForControls) return r;
      return {
        ...r,
        selected_controls: updatedControls,
        existing_controls: controlNames
      };
    });
    updateField('risks', updatedRisks);
    
    setControlLibraryOpen(false);
    setSelectedControls([]);
    setActiveRiskForControls(null);
    toast.success(`Added ${newControls.length} control(s) from library`);
  };

  const removeSelectedControl = (riskId, controlToRemove) => {
    const risk = formData.risks.find(r => r.id === riskId);
    if (!risk) return;
    
    const updatedControls = (risk.selected_controls || []).filter(c => 
      !(c.control_id === controlToRemove.control_id && c.control_name === controlToRemove.control_name)
    );
    const controlNames = updatedControls.map(c => c.control_name).join(', ');
    
    // Update both fields in one operation
    const updatedRisks = formData.risks.map(r => {
      if (r.id !== riskId) return r;
      return {
        ...r,
        selected_controls: updatedControls,
        existing_controls: controlNames
      };
    });
    updateField('risks', updatedRisks);
  };

  const filteredControls = allControls.filter(control => {
    const matchesSearch = !controlSearch || 
      control.control_name.toLowerCase().includes(controlSearch.toLowerCase()) ||
      control.description?.toLowerCase().includes(controlSearch.toLowerCase()) ||
      control.control_id.toLowerCase().includes(controlSearch.toLowerCase());
    const matchesCategory = controlCategory === 'all' || control.category === controlCategory;
    return matchesSearch && matchesCategory;
  });

  // Get unique categories from control library
  const controlCategories = [...new Set(allControls.map(c => c.category))].filter(Boolean);

  // ==================== Render Tab Content ====================
  const handleReviewFlagsChange = (newReviewFlags) => {
    updateField('review_flags', newReviewFlags);
  };

  const renderTabContent = () => {
    switch (currentTab) {
      case 1:
        return (
          <>
            {formData.risk_flags && formData.risk_flags.length > 0 && (
              <AlertFlagsBanner
                riskFlags={formData.risk_flags}
                reviewFlags={formData.review_flags || []}
                onDismiss={handleReviewFlagsChange}
              />
            )}
            <ProjectDetailsStep formData={formData} updateField={updateField} entities={entities} />
          </>
        );
      case 2: 
        return <ScopeStep formData={formData} updateField={updateField} />;
      case 3: 
        return <DataStep formData={formData} updateField={updateField} />;
      case 4: 
        return <LawfulBasisStep formData={formData} updateField={updateField} />;
      case 5: 
        return <DataSharingStep formData={formData} updateField={updateField} />;
      case 6: 
        return <TransfersStep formData={formData} updateField={updateField} />;
      case 7: 
        return <ConsultationStep formData={formData} updateField={updateField} />;
      case 8: 
        return <NecessityStep formData={formData} updateField={updateField} />;
      case 9: 
        return (
          <RisksStep 
            formData={formData} 
            updateField={updateField}
            openRiskLibrary={openRiskLibrary}
            openControlLibrary={openControlLibrary}
            removeSelectedControl={removeSelectedControl}
            estimatingRisks={estimatingRisks}
          />
        );
      case 10: 
        return <OutcomeStep formData={formData} updateField={updateField} />;
      case 11:
        return (
          <>
            <ApprovalsStep formData={formData} updateField={updateField} />
            {id && !isNew && (
              <div className="mt-6">
                <TasksSection
                  sourceModule="dpia"
                  sourceId={id}
                  clientId={formData.client_id}
                  entityId={formData.entity_id}
                  title="Remediation tasks"
                  showProgress
                  progressLabel={undefined}
                />
              </div>
            )}
          </>
        );
      default: 
        return null;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <>
    <AutoTaskPromptModal
      open={taskPromptOpen}
      onOpenChange={setTaskPromptOpen}
      sourceModule="dpia"
      sourceId={taskPromptDpiaId}
    />
    <div className="h-screen flex flex-col bg-background overflow-hidden">
      {/* Workspace Header - h-14 */}
      <header className="h-14 flex-shrink-0 border-b bg-card px-4 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate('/risk/assessments')} className="text-muted-foreground hover:text-foreground">
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div className="border-l pl-4">
            <h1 className="text-base font-semibold text-foreground tracking-tight">
              {isNew ? 'New DPIA Assessment' : formData.dpia_reference || 'Draft'}
            </h1>
            <p className="text-xs text-muted-foreground truncate max-w-xs">
              {formData.project_title || 'Data Protection Impact Assessment'}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {/* Auto-save indicator */}
          {!isNew && (
            <span className="text-xs text-muted-foreground flex items-center gap-1.5">
              {saving ? (
                <>
                  <Loader2 className="h-3 w-3 animate-spin text-primary" />
                  <span>Saving...</span>
                </>
              ) : lastSaved ? (
                <>
                  <Check className="h-3 w-3 text-emerald-500" />
                  <span>Saved {lastSaved.toLocaleTimeString()}</span>
                </>
              ) : null}
            </span>
          )}
          <Badge 
            variant="outline" 
            className={`text-xs font-medium ${
              formData.status === 'approved' ? 'bg-emerald-500/10 text-emerald-500 border-emerald-500/30' : 
              formData.status === 'draft' ? 'bg-muted text-muted-foreground' :
              'bg-primary/10 text-primary border-primary/30'
            }`}
          >
            {formData.status}
          </Badge>
          {/* Export Dropdown */}
          {!isNew && !isReadOnly && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" disabled={exporting} className="h-9" data-testid="dpia-export-btn">
                  {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                  <span className="ml-2 hidden sm:inline">Export</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => handleExport('full')} data-testid="export-full">
                  <FileText className="h-4 w-4 mr-2" />
                  Full Report
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleExport('summary')} data-testid="export-summary">
                  <FileText className="h-4 w-4 mr-2" />
                  Summary Only
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          {!isReadOnly && (
            <Button size="sm" onClick={() => handleSave(true)} disabled={saving} className="h-9">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              <span className="ml-2 hidden sm:inline">Save</span>
            </Button>
          )}
        </div>
      </header>

      {/* Split View: Step Navigator + Form Content */}
      <div className="flex flex-1 overflow-hidden">
        {/* Step Navigator Sidebar - w-64 */}
        <nav className="hidden lg:flex flex-col w-64 bg-muted/50 border-r flex-shrink-0">
          {/* Progress indicator */}
          <div className="p-4 border-b">
            <div className="flex items-center justify-between text-xs text-muted-foreground mb-2">
              <span>Progress</span>
              <span className="font-medium text-foreground">{currentTab} of {TABS.length}</span>
            </div>
            <div className="h-1.5 bg-muted rounded-full overflow-hidden">
              <div 
                className="h-full bg-primary rounded-full transition-all duration-300"
                style={{ width: `${(currentTab / TABS.length) * 100}%` }}
              />
            </div>
          </div>
          
          {/* Step List */}
          <div className="flex-1 overflow-y-auto py-2">
            {TABS.map((tab, index) => {
              const stepNumber = index + 1;
              const isActive = currentTab === stepNumber;
              const isCompleted = stepNumber < currentTab;
              
              return (
                <button
                  key={tab.id}
                  onClick={() => setCurrentTab(stepNumber)}
                  className={`
                    w-full flex items-center gap-3 px-4 py-3 text-left transition-all duration-150
                    ${isActive 
                      ? 'bg-background text-foreground border-r-2 border-primary shadow-sm' 
                      : isCompleted
                        ? 'text-emerald-500 hover:bg-muted'
                        : 'text-muted-foreground hover:bg-muted'
                    }
                  `}
                  data-testid={`step-nav-${stepNumber}`}
                >
                  <div className={`
                    w-7 h-7 rounded-md flex items-center justify-center text-xs font-semibold flex-shrink-0
                    ${isActive 
                      ? 'bg-primary text-primary-foreground' 
                      : isCompleted
                        ? 'bg-emerald-500/20 text-emerald-500'
                        : 'bg-muted text-muted-foreground'
                    }
                  `}>
                    {isCompleted ? <Check className="h-4 w-4" /> : stepNumber}
                  </div>
                  <span className={`text-sm truncate ${isActive ? 'font-semibold' : 'font-medium'}`}>
                    {tab.label}
                  </span>
                </button>
              );
            })}
          </div>
        </nav>

        {/* Mobile Tab Navigation */}
        <div className="lg:hidden border-b bg-muted/50 w-full flex-shrink-0">
          <ScrollArea className="w-full">
            <div className="flex gap-1 p-2">
              {TABS.map((tab, index) => (
                <Button
                  key={tab.id}
                  variant={currentTab === index + 1 ? "default" : "ghost"}
                  size="sm"
                  className="whitespace-nowrap"
                  onClick={() => setCurrentTab(index + 1)}
                >
                  {tab.label}
                </Button>
              ))}
            </div>
          </ScrollArea>
        </div>

        {/* Form Content Area - Centered */}
        <main className="flex-1 flex flex-col overflow-hidden bg-background">
          {/* Step Header - Centered */}
          <div className="flex-shrink-0 py-4 border-b">
            <div className="max-w-4xl mx-auto px-6 lg:px-8">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center">
                  {React.createElement(TABS[currentTab - 1].icon, { className: "h-5 w-5 text-muted-foreground" })}
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-foreground tracking-tight">
                    {TABS[currentTab - 1].label}
                  </h2>
                  <p className="text-sm text-muted-foreground">
                    Step {currentTab} of {TABS.length}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Scrollable Form Content - Centered */}
          <div className="flex-1 overflow-y-auto py-6">
            <div className="max-w-4xl mx-auto px-6 lg:px-8">
              {renderTabContent()}
            </div>
          </div>

          {/* Sticky Footer Navigation - Centered */}
          <div className="flex-shrink-0 py-4 border-t bg-muted/30">
            <div className="max-w-4xl mx-auto px-6 lg:px-8 flex items-center justify-between">
              <Button 
                variant="outline" 
                onClick={prevTab} 
                disabled={currentTab === 1}
                className="h-10"
              >
                <ChevronLeft className="h-4 w-4 mr-2" /> Previous
              </Button>
              <div className="text-sm text-muted-foreground">
                {currentTab < TABS.length ? (
                  <span>Next: <span className="font-medium text-foreground">{TABS[currentTab]?.label}</span></span>
                ) : (
                  <span className="text-emerald-500 font-medium">Final Step</span>
                )}
              </div>
              <Button 
                onClick={nextTab} 
                disabled={currentTab === TABS.length}
                className="h-10"
              >
                Next <ChevronRight className="h-4 w-4 ml-2" />
              </Button>
            </div>
          </div>
        </main>
      </div>

      {/* Risk Library Selection Dialog */}
      <Dialog open={riskLibraryOpen} onOpenChange={setRiskLibraryOpen}>
        <DialogContent className="max-w-4xl max-h-[85vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Library className="h-5 w-5" />
              Select Risks from Library
              {Object.keys(fuzzyRankings).length > 0 && (
                <Badge variant="outline" className="ml-2 text-xs">
                  AI-Ranked
                </Badge>
              )}
            </DialogTitle>
            <DialogDescription>
              {Object.keys(fuzzyRankings).length > 0 
                ? 'Risks are ranked by relevance to your DPIA context. Highly relevant risks are shown first.'
                : 'Choose predefined risks to add to your DPIA. Controls will be automatically suggested.'}
            </DialogDescription>
          </DialogHeader>
          
          {/* Filters */}
          <div className="flex items-center gap-4 py-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search risks..."
                value={librarySearch}
                onChange={(e) => setLibrarySearch(e.target.value)}
                className="pl-9"
              />
            </div>
            {Object.keys(fuzzyRankings).length > 0 && (
              <Button
                variant={showSuggestedOnly ? "default" : "outline"}
                size="sm"
                onClick={() => setShowSuggestedOnly(!showSuggestedOnly)}
              >
                {showSuggestedOnly ? 'Showing Relevant' : 'Show All Risks'}
              </Button>
            )}
            <Select value={libraryCategory} onValueChange={setLibraryCategory}>
              <SelectTrigger className="w-48">
                <SelectValue placeholder="All Categories" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                <SelectItem value="Technical">Technical</SelectItem>
                <SelectItem value="Operational">Operational</SelectItem>
                <SelectItem value="Financial">Financial</SelectItem>
                <SelectItem value="Rights & Freedoms">Rights & Freedoms</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Risk List - with explicit height for scrolling */}
          <div className="flex-1 min-h-0 border rounded-md overflow-hidden">
            <ScrollArea className="h-[400px]">
              {loadingLibrary ? (
                <div className="p-8 text-center text-muted-foreground">
                  <Loader2 className="h-6 w-6 animate-spin mx-auto mb-2" />
                  Loading risk library...
                </div>
              ) : filteredLibraryRisks.length === 0 ? (
                <div className="p-8 text-center text-muted-foreground">
                  <AlertTriangle className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p>No risks found matching your criteria.</p>
                  {showSuggestedOnly && (
                    <Button 
                      variant="link" 
                      size="sm" 
                      onClick={() => setShowSuggestedOnly(false)}
                      className="mt-2"
                    >
                      Click here to show all {libraryCategory !== 'all' ? libraryCategory : ''} risks
                    </Button>
                  )}
                </div>
              ) : (
                <div className="p-2 space-y-2">
                  {filteredLibraryRisks.map((risk) => {
                  const isSelected = selectedLibraryRisks.find(r => r.risk_id === risk.risk_id);
                  const controls = getControlsForRisk(risk.risk_id);
                  const CategoryIcon = risk.category === 'Technical' ? Settings :
                                       risk.category === 'Operational' ? Users :
                                       risk.category === 'Financial' ? DollarSign : Scale;
                  const fuzzyData = fuzzyRankings[risk.risk_id];
                  
                  return (
                    <div
                      key={risk.id}
                      className={`p-3 rounded-lg border cursor-pointer transition-all ${
                        isSelected 
                          ? 'bg-primary/10 border-primary ring-1 ring-primary' 
                          : fuzzyData?.suggestion_level === 'auto_selected'
                            ? 'bg-emerald-50 dark:bg-emerald-950 border-emerald-200 dark:border-emerald-800'
                            : 'hover:bg-muted/50'
                      }`}
                      onClick={() => toggleRiskSelection(risk)}
                    >
                      <div className="flex items-start gap-3">
                        <div className={`mt-0.5 w-5 h-5 rounded border flex items-center justify-center ${
                          isSelected ? 'bg-primary border-primary' : 'border-input'
                        }`}>
                          {isSelected && <Check className="h-3 w-3 text-primary-foreground" />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1 flex-wrap">
                            <Badge variant="outline" className="font-mono text-xs">
                              {risk.risk_id}
                            </Badge>
                            <div className="flex items-center gap-1 text-xs text-muted-foreground">
                              <CategoryIcon className="h-3 w-3" />
                              {risk.category}
                            </div>
                            <Badge className={
                              risk.default_score >= 16 ? 'bg-red-500 text-white' :
                              risk.default_score >= 12 ? 'bg-orange-500 text-white' :
                              risk.default_score >= 8 ? 'bg-yellow-500 text-black' :
                              'bg-green-500 text-white'
                            }>
                              Score: {risk.default_score}
                            </Badge>
                            {fuzzyData && (
                              <Badge 
                                variant={fuzzyData.suggestion_level === 'auto_selected' ? 'default' : 'secondary'}
                                className={fuzzyData.suggestion_level === 'auto_selected' ? 'bg-emerald-600' : ''}
                              >
                                {Math.round(fuzzyData.relevance_score)}% relevant
                              </Badge>
                            )}
                          </div>
                          <h4 className="font-medium text-sm">{risk.risk_name}</h4>
                          <p className="text-xs text-muted-foreground line-clamp-2 mt-1">
                            {risk.description}
                          </p>
                          {controls.length > 0 && (
                            <div className="mt-2 text-xs text-muted-foreground">
                              <span className="font-medium">Suggested controls:</span>{' '}
                              {controls.slice(0, 3).map(c => c.control_name).join(', ')}
                              {controls.length > 3 && ` +${controls.length - 3} more`}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            </ScrollArea>
          </div>

          <DialogFooter className="border-t pt-4">
            <div className="flex items-center justify-between w-full">
              <span className="text-sm text-muted-foreground">
                {selectedLibraryRisks.length} risk(s) selected
              </span>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setRiskLibraryOpen(false)}>
                  Cancel
                </Button>
                <Button 
                  onClick={addRisksFromLibrary} 
                  disabled={selectedLibraryRisks.length === 0}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Selected Risks
                </Button>
              </div>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Control Library Selection Dialog */}
      <Dialog open={controlLibraryOpen} onOpenChange={setControlLibraryOpen}>
        <DialogContent className="max-w-4xl max-h-[85vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5" />
              Select Controls from Library
            </DialogTitle>
            <DialogDescription>
              Choose predefined controls to add to this risk. Controls are organized by category and linked to specific risk types.
            </DialogDescription>
          </DialogHeader>
          
          {/* Filters */}
          <div className="flex items-center gap-4 py-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search controls..."
                value={controlSearch}
                onChange={(e) => setControlSearch(e.target.value)}
                className="pl-9"
              />
            </div>
            <Select value={controlCategory} onValueChange={setControlCategory}>
              <SelectTrigger className="w-48">
                <SelectValue placeholder="All Categories" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {controlCategories.map(cat => (
                  <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Control List */}
          <div className="flex-1 min-h-0 border rounded-md overflow-hidden">
            <ScrollArea className="h-[400px]">
              {loadingControls ? (
                <div className="p-8 text-center text-muted-foreground">
                  <Loader2 className="h-6 w-6 animate-spin mx-auto mb-2" />
                  Loading control library...
                </div>
              ) : filteredControls.length === 0 ? (
                <div className="p-8 text-center text-muted-foreground">
                  <Shield className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p>No controls found matching your criteria.</p>
                </div>
              ) : (
                <div className="p-2 space-y-2">
                  {filteredControls.map((control, idx) => {
                    const key = `${control.control_id}-${control.control_name}`;
                    const isSelected = selectedControls.find(c => 
                      `${c.control_id}-${c.control_name}` === key
                    );
                    const effectivenessColor = 
                      control.effectiveness >= 4 ? 'bg-green-500' :
                      control.effectiveness >= 3 ? 'bg-yellow-500' :
                      'bg-orange-500';
                    
                    return (
                      <div
                        key={`${key}-${idx}`}
                        className={`p-3 rounded-lg border cursor-pointer transition-all ${
                          isSelected 
                            ? 'bg-primary/10 border-primary ring-1 ring-primary' 
                            : 'hover:bg-muted/50'
                        }`}
                        onClick={() => toggleControlSelection(control)}
                      >
                        <div className="flex items-start gap-3">
                          <div className={`mt-0.5 w-5 h-5 rounded border flex items-center justify-center ${
                            isSelected ? 'bg-primary border-primary' : 'border-input'
                          }`}>
                            {isSelected && <Check className="h-3 w-3 text-primary-foreground" />}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1 flex-wrap">
                              <Badge variant="outline" className="font-mono text-xs">
                                {control.control_id}
                              </Badge>
                              <Badge variant="secondary" className="text-xs">
                                {control.category}
                              </Badge>
                              {control.effectiveness && (
                                <div className="flex items-center gap-1 text-xs">
                                  <span className="text-muted-foreground">Effectiveness:</span>
                                  <div className="flex gap-0.5">
                                    {[1, 2, 3, 4, 5].map(n => (
                                      <div 
                                        key={n} 
                                        className={`w-2 h-2 rounded-full ${
                                          n <= control.effectiveness ? effectivenessColor : 'bg-gray-300 dark:bg-gray-600'
                                        }`} 
                                      />
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                            <h4 className="font-medium text-sm">{control.control_name}</h4>
                            {control.description && (
                              <p className="text-xs text-muted-foreground line-clamp-2 mt-1">
                                {control.description}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </ScrollArea>
          </div>

          <DialogFooter className="border-t pt-4">
            <div className="flex items-center justify-between w-full">
              <span className="text-sm text-muted-foreground">
                {selectedControls.length} control(s) selected
              </span>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setControlLibraryOpen(false)}>
                  Cancel
                </Button>
                <Button 
                  onClick={addControlsFromLibrary} 
                  disabled={selectedControls.length === 0}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Selected Controls
                </Button>
              </div>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
    </>
  );
}
